import { useMemo } from 'react';

import { useNow } from '@change-corgi/core-react/date';
import { useI18n } from '@change-corgi/core-react/i18n';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';

import type { ResolverContext } from './heroResolvers';
import { heroResolvers } from './heroResolvers';
import type { HeroActivityType, HeroResult, SpotlightContent } from './types';
import {
	ACTIVITY_TYPES,
	getDaysAgo,
	NEW_PETITION_DAYS_THRESHOLD,
	sortCandidates,
	UNDATED_ACTIVITY_PRIORITY,
} from './utils';

/**
 * Returns all available spotlight items sorted by recency, falling back to a
 * single static item when no activity is present. The first element is always
 * the most prominent content to display.
 */
export function useSpotlightCardContent(): SpotlightContent[] {
	const now = useNow();
	const { translate } = useI18n();
	const { data } = usePetitionDetailsPageData();

	return useMemo(() => {
		const ctx: ResolverContext = {
			data,
			translate,
			spotlightNodes: [
				...data.petition.spotlightEndorsementUpdate.nodes,
				...data.petition.spotlightMediaSourceUpdate.nodes,
				...data.petition.spotlightStarterUpdate.nodes,
			],
		};

		const candidates: Array<[HeroActivityType, HeroResult]> = [];
		for (const activityType of ACTIVITY_TYPES) {
			const result = heroResolvers[activityType](ctx);
			if (result) candidates.push([activityType, result]);
		}

		const sorted = sortCandidates(candidates, UNDATED_ACTIVITY_PRIORITY);
		if (sorted.length > 0) return sorted;

		const { createdAt, spotlightCategory } = data.petition;
		const daysFromCreation = getDaysAgo(now, createdAt) ?? 0;

		const category = spotlightCategory ?? translate('corgi.petition_details.momentum_bar.spotlight.default_category');
		const isNewPetition = daysFromCreation <= NEW_PETITION_DAYS_THRESHOLD;

		return [
			{
				badgeLabel: isNewPetition
					? translate('corgi.petition_details.momentum_bar.spotlight.new_petition_badge')
					: translate('corgi.petition_details.momentum_bar.spotlight.petition_progress_badge'),
				text: translate('corgi.petition_details.momentum_bar.spotlight.gaining_momentum_text', { category }),
				showIcon: true,
			},
		];
	}, [now, translate, data]);
}
